import { useCallback, useRef, useState, UIEvent } from 'react';
import { IconClose } from '../../icon/IconClose';
import { IconButton } from '../../input/IconButton';
import { Input as InputBase } from '../../input/Input';
import { makeTextSearchSuggest } from '../../input/makeTextSearchSuggest';
import { Box } from '../../layout/Box';
import { Divider } from '../../layout/Divider';
import { Flex } from '../../layout/Flex';
import { Typography } from '../../typography/Typography';
import { SCROLL_OFFSET } from './constants';
import { AdvertisingCatalogPageTitle } from './parts/AdvertisingCatalogPageTitle/AdvertisingCatalogPageTitle';
import { AdvertisingCatalogPageProps } from './types';

const Input = makeTextSearchSuggest(InputBase);

export const AdvertisingCatalogPage: React.FC<AdvertisingCatalogPageProps> = ({
    title,
    titleContent,
    description,
    children,
    searchPlaceholder,
    searchValue,
    searchSuggest,
    onChangeSearchInput,
    onApplySearchSuggest,
}) => {
    const scrollableContainerRef = useRef<HTMLDivElement>(null);
    const [isScrolled, setIsScrolled] = useState(false);

    const onScroll = useCallback((event: UIEvent<HTMLDivElement>) => {
        const scrollTop = event.currentTarget.scrollTop;

        setIsScrolled(scrollTop > SCROLL_OFFSET);
    }, []);

    const onResetSearch = useCallback(() => {
        onApplySearchSuggest('');
    }, [onApplySearchSuggest]);

    return (
        <Flex width="100%" height="100%">
            <AdvertisingCatalogPageTitle title={title} />
            {description ? (
                <Typography
                    variant="body"
                    py={isScrolled ? 0 : 2}
                    opacity={isScrolled ? 0 : 1}
                    height={isScrolled ? 0 : undefined}
                    transition="height, opacity, padding 150ms"
                >
                    {description}
                </Typography>
            ) : null}
            <Input
                placeholder={searchPlaceholder}
                variant="outline"
                width="100%"
                value={searchValue}
                onInput={onChangeSearchInput}
                suggest={searchSuggest}
                onApply={onApplySearchSuggest}
                сontainerRef={scrollableContainerRef}
                disableCloseOnScroll
                endAdornment={
                    searchValue ? (
                        <Box alignSelf="center">
                            <IconButton size="xxs" variant="filled" onClick={onResetSearch}>
                                <IconClose />
                            </IconButton>
                        </Box>
                    ) : undefined
                }
            />
            <Divider
                width="calc(100% + 32px)"
                mx={-2}
                px={2}
                mt={1}
                backgroundColor={isScrolled ? 'var(--divider)' : 'none'}
            />
            <Flex
                width="100%"
                height="100%"
                overflowY="auto"
                onScroll={onScroll}
                ref={scrollableContainerRef}
            >
                <Typography variant="title3" weight="bold" py={2}>
                    {titleContent}
                </Typography>
                {children}
            </Flex>
        </Flex>
    );
};
